import {useMutation, useQueryClient} from "@tanstack/react-query";
import {useAuth} from "features/authentication/hooks/useAuth";
import {IPurchase, IUser} from "features/authentication/models/IUser";
import {User} from "features/authentication/models/User";
import {PurchaseService} from "features/authentication/services/PurchaseService/service";
import {getCurrentSiteId} from "features/services/utils/env-helper";
import {SignupParams} from "features/authentication/types.services";
import {getFormattedMessage} from "features/services/utils/language-helper";

export const useAddPurchase = () => {

    const queryClient = useQueryClient();
    const {user} = useAuth();

    return useMutation({
        mutationFn: PurchaseService.addPurchase,
        onMutate: async (params) => {
            if(!user) return;

            if(!params.objectId) {
                throw new Error(getFormattedMessage('jm.components.ClickToBuy.somethingWentWrong'));
            }

            await PurchaseService.addPurchaseMailProxy({
                stageId: params.stageId,
                objectId: params.objectId ? params.objectId : '',
                userId: user.id,
            })


            queryClient.setQueryData(['user'], (oldUser: IUser | undefined) => {
                if (!oldUser) return;

                const updatedUser = {
                    ...oldUser,
                    purchases:  [...(oldUser?.purchases || []),{
                        stageId: params.stageId,
                        objectId: params.objectId,
                        priority: oldUser?.purchases.filter(x => x.stageId === params.stageId).length + 1,
                        purchaseId: `${params.stageId}-${params.objectId}`,
                        site: getCurrentSiteId(),
                        created: new Date(),
                    } as IPurchase],
                };

                return new User(updatedUser);
            });
        }
    })
};

export const useRemovePurchase = ()  => {
    const queryClient = useQueryClient();
    const {user} = useAuth();

    return useMutation({
        mutationFn: PurchaseService.deletePurchase,
        onMutate: async (params: SignupParams ) => {
            if (!user) return;

            queryClient.setQueryData(['user'], (oldUser: IUser | undefined) => {
                if (!oldUser) return;

                let objectStageIndex = 0;
                const updatedPurchases = oldUser?.purchases
                    .filter(purchase => purchase.objectId !== params.objectId)
                    .map((obj) => {
                        if (obj.stageId === params.stageId) {
                            objectStageIndex++;
                            return {
                                ...obj,
                                priority: objectStageIndex,
                            };
                        }
                        return obj;
                    });

                const updatedUser = {
                    ...oldUser,
                    purchases: updatedPurchases,
                };


                return new User(updatedUser);
            });
        }
    });
};

export const useUpdatePurchasePriority = () => {
    const queryClient = useQueryClient();
    const { user } = useAuth();

    return useMutation({
        mutationFn: PurchaseService.updatePriority,
        onMutate: async (params: IPurchase[]) => {
            if (!user) return;

            queryClient.setQueryData(['user'], (oldUser: IUser | undefined) => {
                if (!oldUser) return;

                const updatedPurchasesMap = new Map(
                    params.map((updatedPurchase) => [updatedPurchase.objectId, updatedPurchase]),
                );

                const updatedPurchases = oldUser.purchases.map((purchase) =>
                    updatedPurchasesMap.has(purchase.objectId)
                        ? updatedPurchasesMap.get(purchase.objectId)!
                        : purchase,
                );

                const updatedUser = {
                    ...oldUser,
                    purchases: updatedPurchases,
                };

                return new User(updatedUser);
            });
        },
    });
};
